import { useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useAdminTenants } from '../../../../contexts/AdminEventsProvider';
import { adminTenbisService } from '../../../../services/admin-tenbis/admin-tenbis.service';
import { productsService, type ProductView, type UpdateProductRequest } from '../../../../services/products/products.service';
import { notifyTenbisMenuSync } from '../../../../utils/tenbis-menu-sync-notify';

type UseTenbisMenuSectionArgs = {
  tenantId: string | null;
  employeeSession: string | null;
};

// Mapping list state: manager-session product list + diff-only PATCHes, super-admin menu push.
export function useTenbisMenuSection({ tenantId, employeeSession }: UseTenbisMenuSectionArgs) {
  const { tenants } = useAdminTenants();
  const tenant = tenants.find((entry) => entry.tenantId === tenantId) ?? null;
  const tenbisConnected = Boolean(tenant?.tenbisConfigured) && !tenant?.tenbisAuthFailedAt;
  const canEdit = tenbisConnected && Boolean(employeeSession);

  const [products, setProducts] = useState<ProductView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [savingIds, setSavingIds] = useState<number[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const saveLocksRef = useRef<Set<number>>(new Set());
  const syncLockRef = useRef(false);

  useEffect(() => {
    if (!canEdit || !tenantId || !employeeSession) {
      setProducts([]);
      return;
    }
    let active = true;
    setIsLoading(true);
    setError(null);
    productsService
      .listForSettings(tenantId, employeeSession)
      .then((rows) => {
        if (active) setProducts(rows);
      })
      .catch((loadError: unknown) => {
        if (active) setError(loadError instanceof Error ? loadError.message : 'טעינת המוצרים נכשלה');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [canEdit, tenantId, employeeSession, refreshTick]);

  // PATCH answers 204: the accepted diff is merged into the local row (no list refetch, Rule 22).
  const patchProduct = async (productId: number, diff: UpdateProductRequest) => {
    if (!employeeSession || saveLocksRef.current.has(productId)) {
      return;
    }
    saveLocksRef.current.add(productId);
    setSavingIds((current) => [...current, productId]);
    try {
      await productsService.update(productId, diff, employeeSession);
      setProducts((current) => current.map((product) => (product.id === productId ? { ...product, ...diff } : product)));
    } catch (mutateError: unknown) {
      notifications.show({ color: 'red', message: mutateError instanceof Error ? mutateError.message : 'עדכון המוצר נכשל', autoClose: 4000 });
    } finally {
      saveLocksRef.current.delete(productId);
      setSavingIds((current) => current.filter((id) => id !== productId));
    }
  };

  const toggleIncluded = (product: ProductView) => patchProduct(product.id, { tenbisMenuIncluded: !product.tenbisMenuIncluded });

  // Diff-only: an unchanged id sends nothing; an emptied field clears the mapping (null).
  const saveCatalogId = (product: ProductView, raw: string) => {
    const next = raw.trim();
    if (next === (product.tenbisCatalogId ?? '')) {
      return Promise.resolve();
    }
    return patchProduct(product.id, { tenbisCatalogId: next || null });
  };

  const syncMenu = async () => {
    if (!tenant || syncLockRef.current) {
      return;
    }
    syncLockRef.current = true;
    setIsSyncing(true);
    try {
      notifyTenbisMenuSync(await adminTenbisService.syncMenu(tenant.id));
      setRefreshTick((tick) => tick + 1);
    } catch (syncError: unknown) {
      notifications.show({ color: 'red', message: syncError instanceof Error ? syncError.message : 'סנכרון התפריט לתן ביס נכשל', autoClose: 5000 });
    } finally {
      syncLockRef.current = false;
      setIsSyncing(false);
    }
  };

  return { tenant, tenbisConnected, canEdit, products, isLoading, error, savingIds, isSyncing, toggleIncluded, saveCatalogId, syncMenu };
}
